import { Map } from 'mapbox-gl';
import React, { useState, useEffect } from 'react';
import { useAppConstantsContext } from '@/context/AppConstants';
import { Box, styled } from '@/theme/index';

export interface SatelliteToggleProps {
  map: Map | null;
  autoSwitch?: boolean;
  autoSwitchZoomLevel?: number;
  hideToggle?: boolean;
}

const satelliteToggleConstants = {
  defaultAutoSwitchZoomLevel: 17,
  defaultSatelliteStyle: 'mapbox://styles/mapbox/satellite-v9',
  defaultMapStyle: 'mapbox://styles/mapbox/streets-v12',
};

const ToggleToSatelliteButton = styled(Box)`
  height: 56px;
  width: 56px;
  border: none;
  position: absolute;
  top: 10px;
  right: 50px;
  z-index: 5;
  cursor: pointer;
`;

export function SatelliteToggle({
  map,
  autoSwitch,
  autoSwitchZoomLevel,
  hideToggle,
}: SatelliteToggleProps) {
  const { defaultAutoSwitchZoomLevel, defaultSatelliteStyle, defaultMapStyle } =
    satelliteToggleConstants;
  const {
    links: { mapboxStyleUrl },
    images: { imgToggleToSatellite, imgToggleToStreet },
  } = useAppConstantsContext();
  const zoomToSwitch = autoSwitchZoomLevel || defaultAutoSwitchZoomLevel;
  const mapStyle = mapboxStyleUrl || defaultMapStyle;
  const [satelliteMode, setSatelliteMode] = useState<boolean>(false);
  const [userCurrentMode, setUserCurrentMode] = useState<string>('street');

  const handleToggle = () => {
    if (map && map.getStyle().name !== 'Mapbox Satellite') {
      map.setStyle(defaultSatelliteStyle);
      setSatelliteMode(true);
      setUserCurrentMode('satellite');
    }
    if (map && map.getStyle().name === 'Mapbox Satellite') {
      map.setStyle(mapStyle);
      setSatelliteMode(false);
      setUserCurrentMode('street');
    }
  };

  useEffect(() => {
    const handleZoom = () => {
      if (
        map &&
        map.getZoom() >= zoomToSwitch &&
        map.getStyle().name !== 'Mapbox Satellite'
      ) {
        map.setStyle(defaultSatelliteStyle);
        setSatelliteMode(true);
      }

      if (
        map &&
        map.getZoom() < zoomToSwitch &&
        map.getStyle().name === 'Mapbox Satellite' &&
        userCurrentMode === 'street'
      ) {
        map.setStyle(mapStyle);
        setSatelliteMode(false);
      }
    };

    if (map && autoSwitch) {
      map.on('load', () => {
        map.on('zoomend', handleZoom);
      });
    }

    return () => {
      if (map && autoSwitch) {
        map.off('zoomend', handleZoom);
      }
    };
  }, [
    map,
    autoSwitch,
    zoomToSwitch,
    defaultSatelliteStyle,
    mapStyle,
    userCurrentMode,
  ]);

  if (!hideToggle) {
    return (
      <ToggleToSatelliteButton
        sx={{
          backgroundImage: `url(${
            satelliteMode ? imgToggleToStreet : imgToggleToSatellite
          })`,
        }}
        onClick={handleToggle}
      />
    );
  }

  return null;
}
